"use client";

import React, { useMemo } from "react";
import { useTina, dedupeResourceEdges } from "@/utils";
import Page from "@/components/ui/page";
import Header from "@/components/Header";
import Resources from "@/components/Resources";
import CallToAction from "@/components/CallToAction";

export default function AllResearch(props) {
  const { data: researchData } = useTina({
    query: props.researchPageData?.query,
    variables: props.researchPageData?.variables,
    data: props.researchPageData?.data,
  });

  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { data: categoriesData } = useTina({
    query: props.categoriesData.query,
    variables: props.categoriesData.variables,
    data: props.categoriesData.data,
  });

  const { data: tagsData } = useTina({
    query: props.tagsData.query,
    variables: props.tagsData.variables,
    data: props.tagsData.data,
  });

  const researchPage = researchData?.researchPages;

  // Get selected resource types from CMS config
  const selectedResourceTypes = useMemo(() => {
    return researchPage?.resourceTypes || [];
  }, [researchPage?.resourceTypes]);

  // Get selected category slugs from CMS config (now stored as simple string array)
  const selectedCategorySlugs = useMemo(() => {
    return researchPage?.resourceCategories || [];
  }, [researchPage?.resourceCategories]);

  // Filter categories to only show the selected ones
  const categories = useMemo(() => {
    const allCategories = categoriesData?.categoriesConnection?.edges || [];
    if (selectedCategorySlugs.length === 0) return allCategories;
    return allCategories.filter((cat) =>
      selectedCategorySlugs.includes(cat?.node?.categorySlug)
    );
  }, [categoriesData?.categoriesConnection?.edges, selectedCategorySlugs]);

  // Filter resources by selected types and categories (client-side for real-time CMS updates)
  const resources = useMemo(() => {
    const allResources = dedupeResourceEdges(
      data?.resourcesConnection?.edges
    );

    return allResources.filter((edge) => {
      const resource = edge?.node;

      // Filter by resource type if types are selected
      if (selectedResourceTypes.length > 0) {
        if (!selectedResourceTypes.includes(resource?.templateType)) {
          return false;
        }
      }

      // Filter by category if categories are selected
      if (selectedCategorySlugs.length > 0) {
        const resourceCategories = resource?.categories || [];
        const hasMatchingCategory = resourceCategories.some((cat) =>
          selectedCategorySlugs.includes(cat?.category?.categorySlug)
        );
        if (!hasMatchingCategory) {
          return false;
        }
      }

      return true;
    });
  }, [
    data?.resourcesConnection?.edges,
    selectedResourceTypes,
    selectedCategorySlugs,
  ]);

  return (
    <Page
      defaultTinaField={researchPage}
      seo={researchPage?.seo}
      pagePath="/research/resources"
      titleFallback="Research Resources"
    >
      <Header {...researchPage?.header} variant="left" />

      <Resources
        resources={resources}
        categories={categories}
        tags={tagsData?.tagsConnection?.edges || []}
        showTypeFilters={false}
        showCategoryFilters={true}
      />
      <CallToAction
        {...researchPage?.cta}
        variant="left"
        backgroundVariant="dots"
      />
    </Page>
  );
}
